<template>
    <div class="create-show-screen">
        <h2 class="title">
            About your Podcast
        </h2>

        <p class="description">
            Enter all the important details about your podcast<br>
            You can edit these details at any time inside account settings.
        </p>

        <form class="form" @submit.prevent="handleSubmit">
            <FormRow
                id="title"
                class="show-title"
                label="Podcast Title"
                :error="getValidationError('title')"
            >
                <FormInput
                    id="title"
                    v-model="fields.title"
                    type="text"
                    autocomplete="off"
                    :error="hasValidationError('title')"
                    @focus="clearValidationError('title')"
                />
            </FormRow>

            <FormRow
                id="description"
                class="show-description"
                label="Podcast Description"
                :help="descriptionRemainingCharactersHelp"
                :error="getValidationError('description')"
            >
                <FormRichEditor
                    id="description"
                    v-model="fields.description"
                    name="description"
                    :error="hasValidationError('description')"
                    @focus="clearValidationError('description')"
                />
            </FormRow>

            <FormRow
                id="artwork"
                class="show-artwork"
                label="Episode Artwork"
                help="(JPG or PNG, square image between 1400x1400 and 3000x3000 pixels)"
                :error="getValidationError('artwork')"
            >
                <FormImage
                    id="artwork"
                    ref="customArtwork"
                    :accept="artworkAllowedExtensions"
                    select-label="Upload artwork"
                    change-label="Change artwork"
                    :preview-url="artworkPreview"
                    :error="hasValidationError('artwork')"
                    @change="onArtworkChange"
                />
            </FormRow>

            <FormRow
                id="contact-email"
                class="show-contact-email"
                label="Contact Email"
                :error="getValidationError('contactEmail')"
            >
                <div class="checkbox-component show-use-account-email">
                    <input
                        id="use-account-email"
                        v-model="fields.useAccountEmail"
                        type="checkbox"
                        @change="onUseAccountEmailChange"
                    >
                    <label for="use-account-email" class="checkbox-label">
                        Use Account Email
                    </label>
                </div>

                <FormInput
                    v-if="!fields.useAccountEmail"
                    id="contact-email"
                    v-model="fields.contactEmail"
                    type="email"
                    autocomplete="off"
                    :error="hasValidationError('contactEmail')"
                    @focus="clearValidationError('contactEmail')"
                />
            </FormRow>

            <FormRow
                id="categories"
                class="show-categories"
                label="Genres"
                :error="getValidationError('categories')"
            >
                <FormSelect
                    v-for="index in [0, 1, 2]"
                    :key="index"
                    :options="showCategoriesList"
                    placeholder="Select genre"
                    :value="fields.categories[index]"
                    :error="hasValidationError('categories')"
                    @change="(value) => onCategoryChange(index, value)"
                />
            </FormRow>

            <FormRow
                id="podcast-type"
                class="show-podcast-type"
                label="Podcast Type"
                :error="getValidationError('podcastType')"
            >
                <FormSelect
                    v-model="fields.podcastType"
                    :options="podcastTypesList"
                />
            </FormRow>

            <FormButton
                type="submit"
                class="is-purple"
                :is-loading="isLoading"
                text="Continue"
            />
        </form>
    </div>
</template>
<script>

/**
 * External dependencies.
 */
import { mapState, mapGetters } from 'vuex';
import { uniq } from 'lodash';

/**
 * Internal dependencies.
 */
import {
    FormButton,
    FormImage,
    FormInput,
    FormRichEditor,
    FormRow,
    FormSelect,
} from '@/components/Form';
import formValidation from '@/mixins/formValidation';
import toasts from '@/mixins/toasts';
import {
    artworkAllowedTypes,
    defaults as showDefaults,
    descriptionCharactersLimit,
    podcastTypes,
} from '@/utils/config/showOptions';
import apiCall from '@/utils/apiCall';
import htmlToText from '@/utils/htmlToText';
import { convertToFormData } from '@/utils/formData';
import * as validators from '@/utils/form/showValidation';
import { isFilled } from '@/utils/form/validationRules';

export default {
    components: {
        FormButton,
        FormImage,
        FormInput,
        FormRichEditor,
        FormRow,
        FormSelect,
    },

    mixins: [
        toasts,
        formValidation,
    ],

    props: {
        onSubmit: {
            required: true,
            type: Function,
        },
    },

    data: () => ({
        isLoading: false,
        fields: {},
    }),

    computed: {
        ...mapState('show/modal', [
            'step',
            'show',
        ]),

        ...mapGetters([
            'showCategoriesList',
        ]),

        /**
         * Gets the list of empty fields.
         *
         * @returns {object} Empty fields.
         */
        emptyFields: () => ({
            title: '',
            description: '',
            artwork: undefined,
            useAccountEmail: false,
            contactEmail: '',
            categories: {},
            podcastType: showDefaults.podcastType,
        }),

        /**
         * Computes amount of remaining characters in Description.
         *
         * @returns {number} Amount of remaining characters.
         */
        descriptionRemainingCharacters() {
            return descriptionCharactersLimit - htmlToText(this.fields.description ?? '').length;
        },

        /**
         * Computes help text for description remaining characters.
         *
         * @returns {string} Description remainig charaters help text.
         */
        descriptionRemainingCharactersHelp() {
            if (0 <= this.descriptionRemainingCharacters) {
                return `(${this.descriptionRemainingCharacters} character(s) remaining)`;
            }

            return `(over ${-this.descriptionRemainingCharacters} characters)`;
        },

        /**
         * Returns list of allowed Artwork extensions.
         *
         * @returns {string[]} List of allowed Artwork extensions.
         */
        artworkAllowedExtensions() {
            return Object.keys(artworkAllowedTypes);
        },

        /**
         * Returns list of allowed Artwork MIME types.
         *
         * @returns {string[]} List of allowed Artwork MIME types.
         */
        artworkAllowedMimeTypes() {
            return uniq(Object.values(artworkAllowedTypes));
        },

        /**
         * Gets the artwork preview URL.
         *
         * @returns {string|undefined} Artwork preview URL.
         */
        artworkPreview() {
            return this.fields.artwork instanceof File
                ? URL.createObjectURL(this.fields.artwork)
                : undefined;
        },

        /**
         * Gets the podcast types list.
         *
         * @returns {object[]} Podcast types list.
         */
        podcastTypesList() {
            return podcastTypes;
        },

        /**
         * Gets the list of field validators.
         *
         * @returns {object} Field validators.
         */
        fieldValidators() {
            return {
                title: (value) => validators.validateTitle(value),
                description: (value) => validators.validateDescription(value, {
                    charactersLimit: descriptionCharactersLimit,
                }),
                artwork: (value) => validators.validateArtwork(value, {
                    allowedMimeTypes: this.artworkAllowedMimeTypes,
                }),
                contactEmail: (value, { data }) => {
                    if (! data.useAccountEmail && ! isFilled(value)) {
                        return 'Please provide Contact Email.';
                    }
                },
                podcastType: (value) => validators.validatePodcastType(value),
            };
        },
    },

    /**
     * Handles the component creation.
     */
    created() {
        this.fields = {
            ...this.emptyFields,
            ...this.show,
        };
    },

    methods: {
        /**
         * Handles artwork change.
         *
         * @param  {object} artwork Artwork object.
         */
        onArtworkChange(artwork) {
            this.clearValidationError('artwork');
            this.fields.artwork = artwork;
        },

        /**
         * Handles "use account email" checkbox change.
         */
        onUseAccountEmailChange() {
            this.clearValidationError('contactEmail');
            this.fields.contactEmail = '';
        },

        /**
         * Handles category change.
         *
         * @param  {number} index    Category index.
         * @param  {string} category Category key.
         */
        onCategoryChange(index, category) {
            this.clearValidationError('categories');

            if (! category) {
                this.$delete(this.fields.categories, index);
            } else {
                this.$set(this.fields.categories, index, category);
            }
        },

        /**
         * Handles form submit.
         */
        async handleSubmit() {
            if (! (await this.validate())) {
                return;
            }

            this.isLoading = true;

            const fields = { ...this.fields, hash: undefined };
            const form = convertToFormData(fields, { skipEmpty: true });
            const { status, data } = await (this.fields.hash
                ? this.updateShow(this.fields.hash, form)
                : this.createShow(form)
            );

            this.isLoading = false;

            if (400 <= status) {
                this.handleErrorResponse(data, status);

                return;
            }

            this.onSubmit({
                ...this.fields,
                hash: (data.data ?? data).hash,
            });
        },

        /**
         * Validates fields.
         */
        async validate() {
            this.clearValidationErrors();

            await this.validateForm(this.fields, this.fieldValidators);

            if (this.hasValidationErrors()) {
                this.renderValidationErrorToast();

                return false;
            }

            return true;
        },

        /**
         * Creates show.
         *
         * @param   {FormData} formData Form data.
         * @returns {Promise}           Request promise.
         */
        createShow(formData) {
            return apiCall(this, 'post', '/api/shows', formData);
        },

        /**
         * Updates show.
         *
         * @param   {string}   hash     Show hash.
         * @param   {FormData} formData Form data.
         * @returns {Promise}           Request promise.
         */
        updateShow(hash, formData) {
            return apiCall(this, 'put', `/api/shows/${hash}`, formData);
        },

        /**
         * Handles error response.
         *
         * @param  {object} data   Response data.
         * @param  {number} status Response status code.
         */
        handleErrorResponse(data, status) {
            if (422 === status) {
                this.populateValidationErrors(data.errors ?? {}, (field) => {
                    if (field.startsWith('categories')) {
                        return 'categories';
                    }

                    return field;
                });

                this.renderValidationErrorToast();

                return;
            }

            this.toastError({
                message: {
                    title: 'An unknown error ocurred. Please contact support.',
                },
            });
        },

        /**
         * Renders validation error toast.
         */
        renderValidationErrorToast() {
            this.toastError({
                message: {
                    title: 'There are errors in the form. Please resolve them.',
                },
            });
        },
    },
};
</script>
<style scoped lang="scss">
    @use './CreateShowScreen.scss';
</style>
